import React from "react";
import clsx from "clsx";

import styles from "./layout.module.scss";

type LinkPillProps = {
	icon: React.ReactNode;
	label: string;
	href?: string;
	onClick?: React.MouseEventHandler<HTMLElement>;
	disabled?: boolean;
	className?: string;
};

/**
 * Chip for an external system (Zendesk, Jaris, Flute) shown in the sub-row of a
 * page header. Renders an anchor when given an href, a button when the target
 * has to be resolved on click, and a plain span when there is nothing to open —
 * the pill is then a read-only indicator that the account exists in that system.
 */
export const LinkPill = ({
	icon,
	label,
	href,
	onClick,
	disabled = false,
	className,
}: LinkPillProps) => {
	const content = (
		<>
			<span className={styles.linkPillIcon}>{icon}</span>
			<span className={styles.linkPillLabel}>{label}</span>
		</>
	);

	if (href) {
		return (
			<a
				href={href}
				className={clsx(styles.linkPill, className)}
				target="_blank"
				rel="noreferrer"
			>
				{content}
			</a>
		);
	}

	if (onClick) {
		const handleOnClick = (e: React.MouseEvent<HTMLElement>) => {
			// detail counts consecutive clicks; ignore the repeats of a double-click so the
			// target isn't opened twice. Keyboard activation reports 0 and passes through.
			if (disabled || e.detail > 1) {
				return;
			}

			onClick(e);
		};

		return (
			<button
				type="button"
				className={clsx(styles.linkPill, className)}
				onClick={handleOnClick}
				disabled={disabled}
			>
				{content}
			</button>
		);
	}

	return (
		<span
			className={clsx(styles.linkPill, styles.linkPillStatic, className)}
		>
			{content}
		</span>
	);
};
